import { EPUB_ANNOTATION_PRESENTATION } from 'constants';
import { DEFAULT_COLOR } from 'constants/appStyling/options';
import UserContentItemWithLocation from '../UserContentItemWithLocation';

class UserContentNote extends UserContentItemWithLocation {
  static typeName = 'notes';

  color;
  originalColor;
  snippet;
  presentation;
  userText;

  isReadOnly = false;

  constructor(properties = {}) {
    super(...arguments);

    this.color =
      this.isSaved && this.isPersisted
        ? (properties.color ?? DEFAULT_COLOR)
        : properties.color;
    this.originalColor = this.color; // for preserving old V6 highlight/note combination models

    this.snippet = properties.snippet;
    this.presentation =
      properties.presentation || EPUB_ANNOTATION_PRESENTATION.NOTE;

    this.userText = properties.userText;
    this.userFirstName = properties.firstName ?? null;
    this.userLastName = properties.lastName ?? null;
  }

  serialize() {
    const {
      color,
      originalColor,
      snippet,
      presentation,
      userFirstName,
      userLastName,
      userText,
    } = this;

    return {
      ...super.serialize(),

      color,
      firstName: userFirstName,
      lastName: userLastName,
      originalColor,
      presentation,
      snippet,
      userText,
    };
  }

  get truncatedSnippet() {
    return this.constructor.getTruncatedSnippet(this.snippet);
  }

  async persist({ createMutation, updateMutation, deleteMutation }) {
    const { color, snippet } = this;

    if (this.isPersistedOnce) {
      const isV6HighlightWithNote =
        this.presentation === EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT_WITH_NOTE;

      // Delete note
      if (this.shouldDelete) {
        if (isV6HighlightWithNote) {
          // If v6 note/highlight hybrid, on delete just update
          // record to present as v7 highlight and clear
          // comment - i.e. the note
          await updateMutation({
            variables: {
              input: {
                presentation: EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT, // if it's a v6 model, update it to a v7 model
                comment: '', // clear any associated note text when updating from a v6 to v7 model
              },
              id: this.pk,
            },
          });
        }

        // Otherwise if it's a V7 note that we want to delete, delete it for real.
        await deleteMutation({ variables: { id: this.pk } });

        return null;
      }

      // Update existing note
      if (isV6HighlightWithNote) {
        // The existing model is a v6 highlight/note hybrid
        // Create an additional record for the highlight
        await createMutation({
          variables: {
            input: {
              // preserve original record color for the new highlight record
              color: this.originalColor,

              snippet,
              presentation: EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT,
              comment: '',
              ...this.locationPropertiesForGraphQL,
            },
          },
        });
      }

      const {
        data: { updateEpubAnnotation },
      } = await updateMutation({
        variables: {
          id: this.pk,
          input: {
            color,

            // if it's a v6 model, update it to a v7 model
            presentation: EPUB_ANNOTATION_PRESENTATION.NOTE,

            comment: this.userText,
          },
        },
      });

      if (!updateEpubAnnotation.epubAnnotation) {
        console.warn(
          'Failed to update an EpubAnnotation record through GraphQL. It may not exist in the database.',
        );

        return null;
      }

      return updateEpubAnnotation.epubAnnotation.id;
    }

    // Save a new note
    const {
      data: { createEpubAnnotation },
    } = await createMutation({
      variables: {
        input: {
          color,
          snippet,
          presentation: EPUB_ANNOTATION_PRESENTATION.NOTE,
          comment: this.userText,
          ...this.locationPropertiesForGraphQL,
        },
      },
    });

    return createEpubAnnotation.epubAnnotation.id;
  }
}

export default UserContentNote;
